import { apiRequest } from '@/api/client'
import {
  producttypeFunnelPageSchema,
  producttypeStatisticsSchema,
  topicDecisionSchema,
  topicPageSchema,
} from '@/api/topics/schemas'
import type {
  ProducttypeFunnelPage,
  ProducttypeFunnelQuery,
  ProducttypeStatistics,
  ProducttypeStatisticsQuery,
  TopicDecision,
  TopicPage,
  TopicQuery,
} from '@/api/topics/types'

/**
 * Route-Präfix der Sichtungs-API.
 *
 * Der Controller liegt im Modul AiAgents, nicht in Platform. Dazu der
 * Controller-Name in PascalCase, die API nutzt keine kleingeschriebenen
 * URLs. Also weder "api/platform" noch "producttype-topics".
 */
const BASE = '/api/ai-agents/ProducttypeTopics'

export function fetchTopics(query: TopicQuery, signal?: AbortSignal): Promise<TopicPage> {
  return apiRequest<TopicPage>(BASE, {
    schema: topicPageSchema,
    query: {
      status: query.status,
      producttypeKey: query.producttypeKey,
      category: query.category,
      search: query.search,
      withinRankingCap: query.withinRankingCap,
      sortBy: query.sortBy,
      sortDirection: query.sortDirection,
      page: query.page,
      pageSize: query.pageSize,
      includeMonthlyVolumes: query.includeMonthlyVolumes,
    },
    ...(signal ? { signal } : {}),
  })
}

/**
 * Größte Seite, die das Backend herausgibt.
 *
 * Das Repository klemmt PageSize auf 200 (MaxPageSize). Wer mehr
 * anfragt, bekommt trotzdem 200 und merkt nichts davon, weil die
 * Antwort keinen Fehler enthält, sondern nur weniger Zeilen.
 */
export const MAX_PAGE_SIZE = 200

/**
 * Serverseitig aggregierte Produkttyp-Hauptrichter.
 *
 * Ersetzt den früheren Vollabruf aller Themen samt Browser-Aggregation:
 * Statuszahlen und Potenzialsummen kommen bereits fertig gerechnet vom
 * Backend, gefiltert und sortiert nach denselben Parametern wie die
 * einzelnen Themen.
 */
export function fetchProducttypeFunnels(
  query: ProducttypeFunnelQuery,
  signal?: AbortSignal,
): Promise<ProducttypeFunnelPage> {
  return apiRequest<ProducttypeFunnelPage>(`${BASE}/funnels`, {
    schema: producttypeFunnelPageSchema,
    query: {
      status: query.status,
      category: query.category,
      search: query.search,
      withinRankingCap: query.withinRankingCap,
      minPotential: query.minPotential,
      sortBy: query.sortBy,
      sortDirection: query.sortDirection,
      page: query.page,
      pageSize: query.pageSize,
    },
    ...(signal ? { signal } : {}),
  })
}

/**
 * Serverseitig berechneter Statistiküberblick über Produkttyp-Themen.
 *
 * Liefert ein einzelnes Aggregat für die gesamte Treffermenge, nicht
 * nur für eine geladene Seite. `search` und `minPotential` kennt dieser
 * Endpunkt nicht, er dient dem Überblick, nicht der gefilterten Liste.
 */
export function fetchProducttypeStatistics(
  query: ProducttypeStatisticsQuery,
  signal?: AbortSignal,
): Promise<ProducttypeStatistics> {
  return apiRequest<ProducttypeStatistics>(`${BASE}/statistics`, {
    schema: producttypeStatisticsSchema,
    query: {
      status: query.status,
      category: query.category,
      producttypeKey: query.producttypeKey,
      withinRankingCap: query.withinRankingCap,
      from: query.from,
      to: query.to,
    },
    ...(signal ? { signal } : {}),
  })
}

export function approveTopic(topicId: number): Promise<TopicDecision> {
  return apiRequest<TopicDecision>(`${BASE}/${String(topicId)}/approve`, {
    method: 'POST',
    schema: topicDecisionSchema,
  })
}

export function ignoreTopic(topicId: number): Promise<TopicDecision> {
  return apiRequest<TopicDecision>(`${BASE}/${String(topicId)}/ignore`, {
    method: 'POST',
    schema: topicDecisionSchema,
  })
}

export function resetTopic(topicId: number): Promise<TopicDecision> {
  return apiRequest<TopicDecision>(`${BASE}/${String(topicId)}/reset`, {
    method: 'POST',
    schema: topicDecisionSchema,
  })
}
